import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import WindDirectionPreview from './WindDirectionPreview';
import GameCSVModal from './GameCSVModal.jsx';

const COMPETITIONS = ['League', 'Championship', 'Challenge', 'Other'];
const GAME_TYPES = ['Mens', 'Ladies'];
const TEAM_COLORS = ['red', 'blue', 'brown', 'cyan', 'green', 'orange', 'purple'];
const WIND_ANGLES = Array.from({ length: 24 }, (_, i) => String(i * 15));
const THROW_IN_TIMES = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});
const formatColorLabel = (color) => `${String(color).charAt(0).toUpperCase()}${String(color).slice(1)}`;

function secondsToHHMMSS(sec) {
  const total = Number(sec) || 0;

  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  return [h, m, s]
    .map(x => String(x).padStart(2, "0"))
    .join(":");
}

const hhmmssToSeconds = value => {
  if (!/^\d{1,2}:[0-5]\d:[0-5]\d$/.test(value || "")) return NaN;
  const [hh, mm, ss] = value.split(":").map(Number);
  if (ss > 59 || mm > 59) {
    return NaN;
  }

  return ((hh * 60 * 60) + (mm * 60) + ss);
};

function normalizeThrowInToHHMM(v = '00:00:00') {
  const [h = '00', m = '00'] = String(v).split(':');
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
}

function isValidHHMMSS(v) {
  if (!/^\d{1,2}:[0-5]\d:[0-5]\d$/.test(String(v || ''))) return false;
  return true;
}

const EditGameModal = ({ show, onHide, onUpdate, initialData }) => {
  const [formData, setFormData] = useState({});
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showWindConfirm, setShowWindConfirm] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  const awayColorOptions = TEAM_COLORS.filter((c) => c !== formData.HomeTeamColor);
  const firstHalfSecondsLive = hhmmssToSeconds(formData.FirstHalfVideoTime || '');
  const secondHalfSecondsLive = hhmmssToSeconds(formData.SecondHalfVideoTime || '');
  const halfTimeMismatchWarning =
    isValidHHMMSS(formData.FirstHalfVideoTime || '') &&
    isValidHHMMSS(formData.SecondHalfVideoTime || '') &&
    secondHalfSecondsLive < firstHalfSecondsLive
      ? 'Are you sure? 2nd half time is earlier than 1st half.'
      : '';

  useEffect(() => {
    if (!show) {
      setShowCsvUpload(false);
      setPendingFiles([]);
      setShowWindConfirm(false);
      setPendingSubmitData(null);
    }
  }, [show]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        ThrowInTime: normalizeThrowInToHHMM(initialData.ThrowInTime || '00:00:00'),
        Competition: initialData.Competition || 'League',
        GameType: initialData.GameType || 'Mens',
        HomeTeamColor: initialData.HomeTeamColor || 'red',
        AwayTeamColor: initialData.AwayTeamColor || 'blue',
        WindAngle: String(initialData.WindAngle ?? '0'),
        WindStrength: String(initialData.WindStrength ?? ''),
        FirstHalfVideoTime: secondsToHHMMSS(initialData.FirstHalfVideoTime),
        SecondHalfVideoTime: secondsToHHMMSS(initialData.SecondHalfVideoTime),
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'HomeTeamColor') {
      setFormData((f) => {
        const next = { ...f, HomeTeamColor: value };
        if (next.AwayTeamColor === value) {
          next.AwayTeamColor = TEAM_COLORS.find((c) => c !== value) || '';
        }
        return next;
      });
      return;
    }
    setFormData((f) => ({ ...f, [name]: value }));
  };

  const validateForm = () => {
    const {
      GameDate,
      ThrowInTime,
      HomeTeam,
      AwayTeam,
      Competition,
      GameType,
      WindAngle,
      WindStrength,
      HomeTeamColor,
      AwayTeamColor,
      FirstHalfVideoTime,
      SecondHalfVideoTime,
    } = formData;

    if (
      !GameDate ||
      !ThrowInTime ||
      !HomeTeam ||
      !AwayTeam ||
      !Competition ||
      !GameType ||
      !WindAngle ||
      !WindStrength ||
      !HomeTeamColor ||
      !AwayTeamColor ||
      !FirstHalfVideoTime ||
      !SecondHalfVideoTime
    ) {
      toast.error('Please fill in all required fields.');
      return false;
    }

    const today = new Date().toISOString().split('T')[0];
    if (GameDate > today) {
      toast.error('Game date cannot be in the future.');
      return false;
    }
    if (!THROW_IN_TIMES.includes(ThrowInTime)) {
      toast.error('Game Start Time must be a valid HH:MM value.');
      return false;
    }
    if (!TEAM_COLORS.includes(HomeTeamColor) || !TEAM_COLORS.includes(AwayTeamColor)) {
      toast.error('Team colors must be selected from the list.');
      return false;
    }
    if (HomeTeamColor === AwayTeamColor) {
      toast.error('Home Team and Away Team colors must be different.');
      return false;
    }
    if (!isValidHHMMSS(FirstHalfVideoTime)) {
      toast.error('First Half Video Time must be in HH:MM:SS format.');
      return false;
    }
    if (!isValidHHMMSS(SecondHalfVideoTime)) {
      toast.error('Second Half Video Time must be in HH:MM:SS format.');
      return false;
    }
    if (!WIND_ANGLES.includes(String(WindAngle))) {
      toast.error('Wind Direction must be 0-345 in 15-degree steps.');
      return false;
    }
    if (Number.isNaN(Number(WindStrength)) || Number(WindStrength) < 0) {
      toast.error('Wind Strength must be a positive number.');
      return false;
    }
    return true;
  };

  const buildSubmitData = () => {
    const fhSec = hhmmssToSeconds(formData.FirstHalfVideoTime);
    const shSec = hhmmssToSeconds(formData.SecondHalfVideoTime);
    const windChanged =
      String(formData.WindAngle ?? '') !== String(initialData?.WindAngle ?? '') ||
      String(formData.WindStrength ?? '') !== String(initialData?.WindStrength ?? '');
    const gameTypeChanged =
      String(formData.GameType ?? 'Mens') !== String(initialData?.GameType ?? 'Mens');

    return {
      ...formData,
      ThrowInTime: `${formData.ThrowInTime}:00`,
      FirstHalfVideoTime: fhSec,
      SecondHalfVideoTime: shSec,
      GameType: formData.GameType || 'Mens',
      recalculateShotsForWindChange: windChanged || gameTypeChanged,
      id: formData._id,
    };
  };

  const submitUpdate = async (payload) => {
    const didUpdate = await onUpdate(payload);
    if (didUpdate) {
      setShowWindConfirm(false);
      setPendingSubmitData(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData = buildSubmitData();
    if (submitData.recalculateShotsForWindChange) {
      setPendingSubmitData(submitData);
      setShowWindConfirm(true);
      return;
    }

    await submitUpdate(submitData);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered contentClassName="bg-dark text-white">
        <Modal.Header closeButton closeVariant="white" className="border-secondary">
          <Modal.Title>Edit Game</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-white">
          <Form noValidate onSubmit={handleSubmit}>
            <Row className="g-1">
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control className="w-75" type="date" name="GameDate" value={formData.GameDate || ''} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Select className="w-75" name="ThrowInTime" value={formData.ThrowInTime || '00:00'} onChange={handleChange} required>
                    {THROW_IN_TIMES.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Competition</Form.Label>
                  <Row className="g-1">
                    {COMPETITIONS.map((competition) => (
                      <Col xs={6} key={competition}>
                        <Form.Check
                          type="radio"
                          id={`edit-competition-${competition.toLowerCase()}`}
                          name="Competition"
                          label={competition}
                          value={competition}
                          checked={formData.Competition === competition}
                          onChange={handleChange}
                          required
                        />
                      </Col>
                    ))}
                  </Row>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Game Type</Form.Label>
                  <div>
                    {GAME_TYPES.map((gameType) => (
                      <Form.Check
                        key={gameType}
                        type="radio"
                        id={`edit-game-type-${gameType.toLowerCase()}`}
                        name="GameType"
                        label={gameType}
                        value={gameType}
                        checked={formData.GameType === gameType}
                        onChange={handleChange}
                        required
                        className="mb-1"
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 align-items-start">
              <Col md={6}>
                <h6 className="mt-2 mb-3 text-white pb-1 border-bottom border-secondary">Home Team</h6>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light mb-1" style={{ opacity: 0.85 }}>Name</Form.Label>
                  <div className="text-white fw-semibold fs-4 lh-sm">{formData.HomeTeam || '-'}</div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light mb-1" style={{ opacity: 0.85 }}>Color</Form.Label>
                  <Form.Select className="w-50" name="HomeTeamColor" value={formData.HomeTeamColor || 'red'} onChange={handleChange} required>
                    {TEAM_COLORS.map((color) => (
                      <option key={`edit-home-${color}`} value={color}>
                        {formatColorLabel(color)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6} className="border-start border-secondary ps-md-4">
                <h6 className="mt-2 mb-3 text-white pb-1 border-bottom border-secondary">Away Team</h6>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light mb-1" style={{ opacity: 0.85 }}>Name</Form.Label>
                  <div className="text-white fw-semibold fs-4 lh-sm">{formData.AwayTeam || '-'}</div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light mb-1" style={{ opacity: 0.85 }}>Color</Form.Label>
                  <Form.Select className="w-50" name="AwayTeamColor" value={formData.AwayTeamColor || 'blue'} onChange={handleChange} required>
                    {awayColorOptions.map((color) => (
                      <option key={`edit-away-${color}`} value={color}>
                        {formatColorLabel(color)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h6 className="mt-3">Video Time (HH:MM:SS)</h6>
            <Row className="g-1">
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>1st Half</Form.Label>
                  <Form.Control
                    className="w-50"
                    type="text"
                    name="FirstHalfVideoTime"
                    value={formData.FirstHalfVideoTime || '00:00:00'}
                    onChange={handleChange}
                    placeholder="00:00:00"
                    pattern="^\\d{1,2}:[0-5]\\d:[0-5]\\d$"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={5} className="ps-0">
                <Form.Group className="mb-3">
                  <Form.Label>2nd Half</Form.Label>
                  <Form.Control
                    className="w-50"
                    type="text"
                    name="SecondHalfVideoTime"
                    value={formData.SecondHalfVideoTime || '00:00:00'}
                    onChange={handleChange}
                    placeholder="00:00:00"
                    pattern="^\\d{1,2}:[0-5]\\d:[0-5]\\d$"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            {halfTimeMismatchWarning && (
              <Form.Text className="text-warning d-block small mt-n2 mb-2">
                {halfTimeMismatchWarning}
              </Form.Text>
            )}

            <h6 className="mt-3">Wind Details</h6>
            <Row className="g-3 align-items-start">
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>Wind Direction</Form.Label>
                  <Form.Select className="w-50" name="WindAngle" value={String(formData.WindAngle ?? '0')} onChange={handleChange} required>
                    {WIND_ANGLES.map((angle) => (
                      <option key={angle} value={angle}>
                        {angle}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Wind Strength (km/h)</Form.Label>
                  <Form.Control className="w-50" type="number" step="1" min="0" name="WindStrength" value={formData.WindStrength || ''} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={7} className="ps-0">
                <WindDirectionPreview angle={formData.WindAngle} />
              </Col>
            </Row>

            <div className="mt-0 pt-2 border-top border-secondary">
              <div className="mb-2 d-flex align-items-baseline gap-2">
                <h6
                  className="mb-0"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowCsvUpload((v) => !v)}
                >
                  Upload Shot CSV (Optional)
                </h6>
                <Button
                  variant="link"
                  className="p-0 text-light d-inline-flex align-items-center"
                  type="button"
                  onClick={() => setShowCsvUpload((v) => !v)}
                  aria-label={showCsvUpload ? 'Hide CSV upload options' : 'Show CSV upload options'}
                >
                  {showCsvUpload ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                </Button>
              </div>
            </div>
            {showCsvUpload && (
              <div className="mb-3">
                <p className="text-light mb-2">
                  Upload one or more CSV files to add shots for this game.
                </p>
                <Button variant="outline-warning" className="fw-semibold" onClick={() => setShowCsvModal(true)} type="button">
                  Upload CSV
                </Button>
              </div>
            )}
            <div className="mt-3 pt-3 mb-3 border-top border-secondary" />

            <Button type="submit" variant="warning" className="text-dark fw-semibold">
              Save Changes
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <GameCSVModal
        show={showCsvModal}
        onHide={() => setShowCsvModal(false)}
        gameId={formData._id}
        homeTeam={formData.HomeTeam}
        awayTeam={formData.AwayTeam}
        gameType={formData.GameType || 'Mens'}
        pendingFiles={pendingFiles}
        setPendingFiles={setPendingFiles}
      />
      <Modal
        show={showWindConfirm}
        onHide={() => {
          setShowWindConfirm(false);
          setPendingSubmitData(null);
        }}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton closeVariant="white" className="border-secondary">
          <Modal.Title>Confirm Recalculation</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-white">
          Changing the wind or game type will recalculate all shots for this game. Are you sure you want to continue?
        </Modal.Body>
        <Modal.Footer className="border-secondary">
          <Button
            variant="outline-light"
            onClick={() => {
              setShowWindConfirm(false);
              setPendingSubmitData(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            className="text-dark fw-semibold"
            onClick={() => {
              if (pendingSubmitData) {
                submitUpdate(pendingSubmitData);
              }
            }}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditGameModal;
